import React from 'react';
import { AgentDetail } from '../../agent-performance/agentTableTypes';
import { agentMetricsColumns } from '../../agent-performance/tableConfig';
import { cn } from '@/lib/utils';
import { getTransferredRateColor, getSlaColor, formatValue } from '../utils/columnFormatters';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const TOOLTIP_TRANSFERRED_RATE = 'Click to view detailed';
const TOOLTIP_CALL_JOURNEY = 'Click to view detailed call journey';
const TOOLTIP_SLA = 'Click to view detailed';

const FirstColumnTooltip: React.FC<{
  value: string;
  colKey: string;
  item: AgentDetail;
}> = ({ value, colKey, item }) => (
  <Tooltip delayDuration={100}>
    <TooltipTrigger asChild>
      <p className={cn(
        "font-medium text-left pl-4 dark:text-white truncate cursor-pointer",
        colKey === 'transferredRate' && getTransferredRateColor(item.transferredRate),
        colKey === 'sla' && getSlaColor(item.sla)
      )}>
        {value}
      </p>
    </TooltipTrigger>
    <TooltipContent side="top" align="start">
      <p className="text-xs">{value}</p>
    </TooltipContent>
  </Tooltip>
);

interface ClickableMetricCellProps {
  value: string;
  columnKey: string;
  handledCallIds: string[];
  transferredCallIds: string[];
  callIdsSLA?: string[];
  totalSLACalls?: number;
  agentName?: string;
  queueName: string;
  colorClassName?: string;
  onCellClick?: (
    columnKey: string,
    handledCallIds: string[],
    transferredCallIds: string[],
    callIdsSLA?: string[],
    totalSLACalls?: number,
    agentName?: string,
    queueName?: string
  ) => void;
}

const ClickableMetricCell: React.FC<ClickableMetricCellProps> = ({
  value,
  columnKey,
  handledCallIds,
  transferredCallIds,
  callIdsSLA,
  totalSLACalls,
  agentName,
  queueName,
  colorClassName,
  onCellClick,
}) => {
  const callIds = columnKey === 'transferredRate' 
    ? transferredCallIds 
    : columnKey === 'sla' 
    ? callIdsSLA 
    : handledCallIds;
  
  const handleClick = () => {
    if (onCellClick && callIds && callIds.length > 0) {
      onCellClick(columnKey, handledCallIds, transferredCallIds, callIdsSLA, totalSLACalls, agentName, queueName);
    }
  };

  const hasCallIds = callIds && callIds.length > 0;
  const isNoDataValue = value === 'No calls transferred' || value === 'No information';

  return (
    <Tooltip delayDuration={100}>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={cn(
            "text-center px-4 bg-transparent border-0 p-0",
            !isNoDataValue && "font-bold",
            hasCallIds 
              ? "cursor-pointer transition-opacity underline" 
              : "cursor-default",
            colorClassName || "text-[#0C3451] dark:text-[#F4F4F4]"
          )}
          onClick={handleClick}
          disabled={!hasCallIds}
        >
          {value}
        </button>
      </TooltipTrigger>
      {hasCallIds && (
        <TooltipContent side="top" align="start">
          <p className="text-xs">
            {columnKey === 'transferredRate' 
              ? TOOLTIP_TRANSFERRED_RATE 
              : columnKey === 'sla' 
              ? TOOLTIP_SLA 
              : TOOLTIP_CALL_JOURNEY}
          </p>
        </TooltipContent>
      )}
    </Tooltip>
  );
};

interface AgentMetricsExpandedRowProps {
  details: AgentDetail[];
  backgroundColor?: string;
  isLoading?: boolean;
  agentName?: string;
  onCellClick?: (
    columnKey: string,
    handledCallIds: string[],
    transferredCallIds: string[],
    callIdsSLA?: string[],
    totalSLACalls?: number,
    agentName?: string,
    queueName?: string
  ) => void;
}

export const AgentMetricsExpandedRow: React.FC<AgentMetricsExpandedRowProps> = ({
  details,
  backgroundColor = 'bg-gray-50 dark:bg-[#3C5C73]',
  isLoading = false,
  agentName,
  onCellClick,
}) => {
  
  // Show loading state
  if (isLoading) {
    return (
      <div className={cn("table-row mt-1", backgroundColor)}>
        {agentMetricsColumns.map((col, colIndex) => (
          <div
            key={col.key}
            className="table-cell h-[45px] px-2 text-xs border-gray-300 dark:border-white border-b py-2"
            style={{
              minWidth: col.minWidth,
              maxWidth: col.maxWidth,
              width: colIndex === 0 ? 'auto' : `${100 / agentMetricsColumns.length}%`,
            }}
          >
            <div className="h-4 w-full bg-gray-200 animate-pulse rounded" />
          </div>
        ))}
      </div>
    );
  }

  // Show data rows
  return (
    <TooltipProvider delayDuration={300}>
      {details && details.length > 0 ? (
        details.map((item, itemIndex) => (
          <div key={itemIndex} className={cn("table-row border-b border-gray-300 dark:border-white", backgroundColor)}>
            {agentMetricsColumns.map((col, colIndex) => {
              const formattedValue = formatValue(col.key, item);
              const isNoDataValue = formattedValue === 'No calls transferred' || formattedValue === 'No information';
              
              return (
              <div
                key={col.key}
                className={cn(
                  'table-cell min-h-[45px] px-2 text-xs text-teamscx-primary dark:text-white border-gray-300 dark:border-white transition-colors border-b align-middle',
                  colIndex === 0 && "px-6",
                  (col.isClickable && !isNoDataValue) ? "px-20" : "px-8",
                  (col.isClickable && col.key === 'sla') && "px-6",
                )}
                style={{
                  minWidth: colIndex === 0 ? '200px' : col.minWidth,
                  maxWidth: colIndex === 0 ? '265px' : col.maxWidth,
                  width: colIndex === 0 ? '240px' : `calc((100% - 240px) / ${agentMetricsColumns.length - 1})`,
                  display: colIndex === 0 ? 'flex' : '',
                  alignItems: colIndex === 0 ? 'center' : '',
                }}
              >
                {colIndex === 0 ? (
                  <FirstColumnTooltip 
                    value={formattedValue}
                    colKey={col.key}
                    item={item}
                  />
                ) : (
                  col.isClickable ? (
                    <ClickableMetricCell
                      value={formattedValue}
                      columnKey={col.key}
                      handledCallIds={item.callIdsHandledCallsByCQ || []}
                      transferredCallIds={item.callIdsTransferredRate || []}
                      callIdsSLA={item.callIdsSLA || []}
                      totalSLACalls={item.totalSLaCalls || 0}
                      agentName={agentName}
                      queueName={item.queueName}
                      colorClassName={
                        col.key === 'transferredRate' 
                          ? getTransferredRateColor(item.transferredRate) 
                          : col.key === 'sla' 
                          ? getSlaColor(item.sla) 
                          : undefined
                      }
                      onCellClick={onCellClick}
                    />
                  ) : (
                    <p 
                      className={cn(
                        "font-medium text-left px-4 dark:text-white",
                        col.key === 'lastCallTakenSecond' && 'px-0',
                        col.key === 'transferredRate' && getTransferredRateColor(item.transferredRate),
                        col.key === 'sla' && getSlaColor(item.sla)
                      )}
                    >
                      {formattedValue}
                    </p>
                  )
                )}
              </div>
              );
            })}
          </div>
        ))
      ) : (
        <div className={cn("table-row", backgroundColor)} style={{ position: 'relative' }}>
          <div 
            className={cn(
              "absolute inset-0 h-[45px] flex items-center justify-center text-xs text-gray-500 border-gray-300 dark:border-white border-b dark:text-white",
              backgroundColor
            )}
            style={{ zIndex: 1 }}
          >
            No call records were found 
          </div>
          {agentMetricsColumns.slice(1).map((col) => (
            <div 
              key={col.key} 
              className="table-cell border-gray-300 dark:border-white border-b opacity-0"
              style={{
                minWidth: col.minWidth,
                maxWidth: col.maxWidth,
                height: '45px'
              }}
            />
          ))}
        </div>
      )}
    </TooltipProvider>
  );
};
